import "./CompleteTaskModal.css";

import { Dispatch, FormEvent, SetStateAction, useEffect, useRef, useState } from "react";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData } from "../../../../models";
import { searchOrganisationUsers } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { createWorkflowTask, updateWorkflowTask } from "../../../../service/workflow";
import { useAuth } from "../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../utils";
import { Toast } from "../../../../widget";
import { getTaskVisibilityFromAcl } from "../../getTaskVisibility";

/*
  REQ-008 — a general task is repeatable on completion, so the modal runs in two steps: record the
  resolution, then offer to clone the task into its next instance. A workflow task has no repeat-on-
  completion flow and finishes at the first step.
*/
export type CompleteTaskModalStep = "resolution" | "repeat";

// The fields the next instance is cloned from. A Pick rather than the whole task, so that a caller
// holding the completed task in some other shape (a search result, say) can supply one.
export type RepeatableTaskFields = Pick<
  TaskPanelData,
  | "name"
  | "description"
  | "isMandatory"
  | "isMilestone"
  | "keyDate"
  | "objectType"
  | "objectUuid"
  | "ownerOrganisationUuid"
  | "repeat"
>;

// The completed task as it was at the moment it was saved, which is what the clone is built from.
// Snapshotted because handleRefresh re-seeds the panel's currentTask behind the open modal.
export interface CompleteTaskRepeatSource {
  task: RepeatableTaskFields;
  // The audience resolved for the completed task — the clone has to be readable by the same organisations.
  visibility: string[];
}

interface UseCompleteTaskModalProps {
  closeModal: () => void;
  closePanel: () => void;
  handleRefresh: () => void;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>> | undefined;
  task: TaskPanelData | undefined;
  /*
    A caller that owns the updates reports back one repeat source per repeatable general task it
    completed, so the repeat step can be offered for tasks this hook didn't write itself. They are
    offered in the order given; an empty list (or nothing) means the completion is finished with.
  */
  onSubmitResolution: ((resolution: string) => Promise<CompleteTaskRepeatSource[] | void> | void) | undefined;
}

interface UseCompleteTaskModalReturnData {
  handleCloseModal: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;

  setResolution: Dispatch<SetStateAction<string | undefined>>;
  resolution: string | undefined;

  isHandleSubmitLoading: boolean;

  // Repeat step (REQ-008)
  step: CompleteTaskModalStep;
  completedTaskName: string | null | undefined;
  // Which of the completed tasks this step is offering, when a drop completed more than one of them.
  repeatNumber: number;
  repeatTotal: number;
  repeatDeadlineDate: Date | undefined;
  setRepeatDeadlineDate: Dispatch<SetStateAction<Date | undefined>>;
  repeatAssigneeUuid: string | null;
  repeatAssigneeName: string | null;
  // The task can only be assigned by the organisation that will own it, so the picker is hidden otherwise.
  canAssignRepeat: boolean;
  assigneeList: SelectData;
  handleAssigneeSearch: (search: string) => Promise<void>;
  handleSelectAssignee: (value: string) => void;
  handleClearAssignee: () => void;
  handleSkipRepeat: () => void;
  handleCreateRepeat: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  isHandleCreateRepeatLoading: boolean;
}

export const useCompleteTaskModal = ({
  closeModal,
  closePanel,
  handleRefresh,
  setErrors,
  task,
  onSubmitResolution,
}: UseCompleteTaskModalProps): UseCompleteTaskModalReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [isHandleCreateRepeatLoading, setIsHandleCreateRepeatLoading] = useState(false);
  const [resolution, setResolution] = useState<string>();

  const [step, setStep] = useState<CompleteTaskModalStep>("resolution");
  /*
    The tasks still to be offered a repeat, and which of them the step is on. A queue because one drop
    can complete several general tasks at once, and each needs its own deadline and assignee — they are
    offered one after another rather than the first one only.
  */
  const [repeatQueue, setRepeatQueue] = useState<CompleteTaskRepeatSource[]>([]);
  const [repeatIndex, setRepeatIndex] = useState(0);
  const [repeatDeadlineDate, setRepeatDeadlineDate] = useState<Date>();
  const [repeatAssigneeUuid, setRepeatAssigneeUuid] = useState<string | null>(null);
  const [repeatAssigneeName, setRepeatAssigneeName] = useState<string | null>(null);
  const [assigneeList, setAssigneeList] = useState<SelectData>([]);
  // Typeahead searches can resolve out of order — only the newest one may write the list.
  const assigneeSearchRequestId = useRef(0);

  // The current task's own inputs — cleared between the queued tasks, since each is set afresh.
  const resetRepeatInputs = (): void => {
    setRepeatDeadlineDate(undefined);
    setRepeatAssigneeUuid(null);
    setRepeatAssigneeName(null);
  };

  const resetState = (): void => {
    setResolution(undefined);
    setStep("resolution");
    setRepeatQueue([]);
    setRepeatIndex(0);
    resetRepeatInputs();
    setAssigneeList([]);
  };

  const handleClosePanel = (): void => {
    resetState();
    if (setErrors) setErrors([]);
    closePanel();
  };

  const handleCloseModal = (): void => {
    resetState();
    closeModal();
  };

  /*
    Quick-complete callers build their TaskPanelData from a grid row, which carries no visibility, and an
    update rewrites the field: a project workflow task is rejected for missing its owner organisation
    (BR-02232), and a general task's readWorkflowTask grants are re-derived from what is sent, so an empty
    list would leave the completed task unreadable. Read the current grants when none were supplied.
  */
  const getVisibility = async (currentTask: TaskPanelData): Promise<string[] | undefined> => {
    if (currentTask.visibility?.length) return currentTask.visibility;
    if (!currentTask.uuid) return [];

    return getTaskVisibilityFromAcl(currentTask.uuid, currentTask.ownerOrganisationUuid);
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!resolution) return;

    /*
      A caller completing more than one task at once owns the updates itself — this hook writes a single
      task, so it would have nothing to send them against. It then collects the Resolution and nothing
      more, and the caller closes the modal once its own updates have settled.
    */
    if (onSubmitResolution) {
      const callerRepeatSources = await onSubmitResolution(resolution);
      setResolution(undefined);

      /*
        REQ-008 — a general task is repeatable however it was completed, so a caller hands back what the
        next instance is cloned from for each general task it completed, and the modal stays open on the
        repeat step to work through them. A drop of nothing but workflow tasks is finished with here.
      */
      if (callerRepeatSources?.length) {
        setRepeatQueue(callerRepeatSources);
        setRepeatIndex(0);
        setStep("repeat");
      }

      return;
    }

    if (task) {
      // A general task (no workflow step) hangs off its parent object via objectType/objectUuid;
      // a workflow task uses the same pair for its optional activity link. Completing the task
      // has to resend whichever applies, otherwise the relation is cleared.
      const isGeneralTask = task.stepUuid === null;
      const activityRelationType = task.relateToActivity ? "Activity" : null;
      const objectType = isGeneralTask ? task.objectType : activityRelationType;
      const objectUuid = isGeneralTask ? task.objectUuid : task.activityUuid;

      /*
        The update rewrites the audience from what is sent, so completing the task on a visibility the
        ACL couldn't be read for would revoke everyone else's access. Nothing is sent in that case.
      */
      const visibility = await getVisibility(task);
      if (!visibility) {
        Toast.error({ message: "Couldn't read who this task is shared with - please try again later." });
        handleCloseModal();
        return;
      }

      const res = await updateWorkflowTask({
        assignedToUserUuid: task.assignedToUserUuid,
        deadlineDate: task.deadlineDate,
        description: task.description,
        isMandatory: task.isMandatory ?? false,
        isMilestone: task.isMilestone ?? false,
        keyDate: task.keyDate,
        name: task.name ?? "",
        objectType,
        objectUuid,
        reminderDate: null,
        reminderType: null,
        repeat: task.repeat ?? false,
        visibility,
        ownerOrganisationUuid: task.ownerOrganisationUuid ?? "",
        rowVersion: task.rowVersion ?? 1,
        resolution,
        status: TaskConstants.TASK_STATUS_COMPLETE,
        workflowTaskUuid: task.uuid ?? "",
        // Null (not "") for a general task — the backend routes on WorkflowStepUuid == null.
        workflowStepUuid: task.stepUuid,
      });
      if (res.status === Status.Success && res.data) {
        Toast.success({
          message: `Workflow task completed successfully`,
        });
        handleRefresh();

        /*
          REQ-008 — the completed general task keeps its resolution and is done with; the modal stays open
          on the repeat step so the next instance can be created from it. A parent is required, since the
          clone hangs off the same object, and only the owning organisation can create against it.
        */
        const canRepeat =
          task.stepUuid === null && task.objectType !== null && task.objectUuid !== null && visibility.length > 0;

        if (canRepeat) {
          setRepeatQueue([{ task, visibility }]);
          setRepeatIndex(0);
          setStep("repeat");
          return;
        }

        handleClosePanel();
      }

      if (res.status === Status.Error && res.errors) {
        if (setErrors) setErrors(res.errors);
        handleCloseModal();
      }
    }
  }, setIsHandleSubmitLoading);

  // The task the repeat step is currently offering.
  const repeatSource = repeatQueue[repeatIndex];

  // Only the organisation that will own the new task may assign it — mirrors the create path in useTaskSubmit.
  const canAssignRepeat =
    currentOrganisationUuid !== null &&
    currentOrganisationUuid !== undefined &&
    currentOrganisationUuid === repeatSource?.task.ownerOrganisationUuid;

  const handleAssigneeSearch = async (search: string): Promise<void> => {
    if (!currentOrganisationUuid) return;

    assigneeSearchRequestId.current += 1;
    const requestId = assigneeSearchRequestId.current;

    try {
      const response = await searchOrganisationUsers({
        organisationUuid: currentOrganisationUuid,
        paging: { limit: 50, beforeCursor: "", afterCursor: "" },
        sort: [{ key: "user.fullName" as "results.user.fullName", direction: "asc" }],
        filter: {
          results: {
            active: { operator: "eq", value: true },
            user: { fullName: { operator: "startsWith", value: search } },
          },
        },
      });

      // A slower earlier search must not overwrite the results for what is now in the box.
      if (requestId !== assigneeSearchRequestId.current) return;

      /*
        The lookup resolves with `Status.Error` rather than rejecting, so a failure used to fall
        through to the empty list below and read as "this organisation has no matching users" — the
        one thing it does not mean. Said out loud instead, so an empty picker is never mistaken for
        an answer.
      */
      if (response.status !== Status.Success || !response.data?.results) {
        setAssigneeList([]);
        Toast.error({ message: "Couldn't load the list of users to assign to - please try again later." });
        return;
      }

      setAssigneeList(
        response.data.results.flatMap((el) =>
          el.user.uuid && el.user.fullName ? { key: el.user.uuid, value: el.user.fullName } : []
        )
      );
    } catch {
      if (requestId !== assigneeSearchRequestId.current) return;

      setAssigneeList([]);
      Toast.error({ message: "Couldn't load the list of users to assign to - please try again later." });
    }
  };

  const handleSelectAssignee = (value: string): void => {
    /*
      The Autocomplete reports an empty key on every keystroke, not only when its text is cleared —
      but editing the text abandons the selection all the same, so the stored user goes with it.
      Holding onto it instead let the two disagree: type over a chosen assignee and submit, and the
      task was created for the previous user while the box showed something else. The Clear button
      remains the explicit way to unassign; this only stops a selection outliving the text it came
      from.
    */
    if (!value) {
      setRepeatAssigneeUuid(null);
      setRepeatAssigneeName(null);
      return;
    }

    setRepeatAssigneeUuid(value);
    setRepeatAssigneeName(assigneeList.find((user) => user.key === value)?.value?.toString() ?? null);
  };

  const handleClearAssignee = (): void => {
    setRepeatAssigneeUuid(null);
    setRepeatAssigneeName(null);
  };

  // Prime the picker so the repeat step opens with the organisation's users already listed, rather than
  // an empty dropdown until the first keystroke.
  useEffect(() => {
    const primeAssigneeList = async (): Promise<void> => {
      await handleAssigneeSearch("");
    };

    if (step === "repeat" && canAssignRepeat) {
      primeAssigneeList();
    }
  }, [step, canAssignRepeat]);

  /*
    Done with the task the step is on, whether it was repeated or skipped: move to the next task waiting
    for one, with its inputs cleared so the new deadline and assignee are set for that task rather than
    inherited from the last. The modal only closes once the queue is exhausted.
  */
  const advanceRepeat = (): void => {
    if (repeatIndex + 1 < repeatQueue.length) {
      setRepeatIndex(repeatIndex + 1);
      resetRepeatInputs();
      return;
    }

    handleClosePanel();
  };

  const handleSkipRepeat = (): void => {
    advanceRepeat();
  };

  /*
    REQ-008 — clone the repeatable fields of the completed task into a new general task. The new task
    carries no completion data: no resolution, no rowVersion, and a fresh status. Reminders are dropped
    with the old deadline they were set against.
  */
  const handleCreateRepeat = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!repeatSource || !repeatDeadlineDate) return;

    const { task: completedTask, visibility } = repeatSource;
    const assignedToUserUuid = canAssignRepeat ? repeatAssigneeUuid : null;

    const res = await createWorkflowTask({
      assignedToUserUuid,
      deadlineDate: repeatDeadlineDate,
      description: completedTask.description,
      isMandatory: completedTask.isMandatory ?? false,
      isMilestone: completedTask.isMilestone ?? false,
      keyDate: completedTask.keyDate,
      name: completedTask.name ?? "",
      objectType: completedTask.objectType,
      objectUuid: completedTask.objectUuid,
      ownerOrganisationUuid: completedTask.ownerOrganisationUuid,
      reminderDate: null,
      reminderType: null,
      repeat: completedTask.repeat ?? false,
      visibility,
      status: assignedToUserUuid ? TaskConstants.TASK_STATUS_TO_DO : TaskConstants.TASK_STATUS_UNASSIGNED,
      // Null — the backend routes on WorkflowStepUuid == null to file this as a general task.
      workflowStepUuid: null,
    });

    if (res.status === Status.Success && res.data) {
      Toast.success({ message: "General task repeated successfully" });
      handleRefresh();
      advanceRepeat();
      return;
    }

    /*
      The completed task is already saved, so a failure here only costs the repeat. Report it and leave the
      step open to retry rather than closing and losing what was entered.
    */
    if (setErrors) setErrors(res.errors);
    Toast.error({
      message: `Task completed, but the repeated task could not be created${
        res.errors?.[0]?.message ? `: ${res.errors[0].message}` : ""
      }`,
    });
  }, setIsHandleCreateRepeatLoading);

  return {
    handleCloseModal,
    handleSubmit,
    setResolution,
    resolution,
    isHandleSubmitLoading,

    step,
    completedTaskName: repeatSource?.task.name ?? task?.name,
    repeatNumber: repeatIndex + 1,
    repeatTotal: repeatQueue.length,
    repeatDeadlineDate,
    setRepeatDeadlineDate,
    repeatAssigneeUuid,
    repeatAssigneeName,
    canAssignRepeat,
    assigneeList,
    handleAssigneeSearch,
    handleSelectAssignee,
    handleClearAssignee,
    handleSkipRepeat,
    handleCreateRepeat,
    isHandleCreateRepeatLoading,
  };
};
